import React, { useState } from 'react';
import { FadeIn } from '../components/ui/FadeIn';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck } = FiIcons;

const plans = [
  {
    name: 'Starter',
    label: 'For Creators',
    priceMo: 39,
    priceYr: 31,
    features: [
      '10 transformations/month',
      'Videos up to 60 minutes',
      '5 built-in voices',
      'Brand kit (1)',
      'PNG + PDF slide export',
      'Watermark-free video',
      'Email support'
    ]
  },
  {
    name: 'Professional',
    label: 'For Course Creators',
    priceMo: 99,
    priceYr: 79,
    highlight: true,
    features: [
      '35 transformations/month',
      'Unlimited video length',
      'Custom voice upload (ElevenLabs)',
      'Brand kits (3)',
      'Full export: PNG + PDF + PPTX',
      'Motion video add-on ($4/trans.)',
      'Priority processing queue',
      'Chat support'
    ]
  },
  {
    name: 'Business',
    label: 'For Teams & Agencies',
    priceMo: 249,
    priceYr: 199,
    features: [
      '80 transformations/month',
      '20 motion video credits included',
      'White-label (custom subdomain)',
      'Team seats (up to 5)',
      'API access (1,000 req/mo)',
      'Unlimited brand kits',
      'Analytics dashboard',
      'Dedicated Slack support'
    ]
  }
];

const faqs = [
  {
    q: "What counts as one transformation?",
    a: "Processing one video URL from start to complete slide deck + video output = 1 transformation, regardless of video length."
  },
  {
    q: "Can I try before paying?",
    a: "Yes — free plan includes 1 transformation with watermark. No credit card required."
  },
  {
    q: "What voices are available?",
    a: "5 built-in professional voices on Starter. Pro/Business allows uploading your own voice sample for ElevenLabs custom voice synthesis."
  },
  {
    q: "Does the API work with my LMS?",
    a: "Yes — REST API with webhook support integrates with TalentLMS, Docebo, Moodle, Teachable, Kajabi, and any custom platform."
  }
];

const Pricing = () => {
  const [isAnnual, setIsAnnual] = useState(true);

  return (
    <div className="pt-20 pb-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <FadeIn className="text-center mb-16">
        <h1 className="text-4xl md:text-6xl font-black mb-6 text-white">Simple pricing. Powerful output.</h1>
        <p className="text-xl text-white/70">Start free. Scale when you're ready. No surprise fees.</p>

        {/* Toggle */}
        <div className="flex items-center justify-center gap-4 mt-10">
          <span className={`text-sm ${!isAnnual ? 'text-white font-bold' : 'text-white/60'}`}>Monthly</span>
          <button
            onClick={() => setIsAnnual(!isAnnual)}
            className="w-14 h-8 bg-brand-card rounded-full p-1 relative transition-colors border border-brand-border"
          >
            <div
              className={`w-6 h-6 bg-brand-electric rounded-full transition-transform ${
                isAnnual ? 'translate-x-6' : 'translate-x-0'
              }`}
            ></div>
          </button>
          <span className={`text-sm flex items-center gap-2 ${isAnnual ? 'text-white font-bold' : 'text-white/60'}`}>
            Annual
            <span className="text-xs text-brand-mint bg-brand-mint/10 px-2 py-0.5 rounded-full">Save 20%</span>
          </span>
        </div>
      </FadeIn>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
        {plans.map((plan, idx) => (
          <FadeIn key={idx} delay={idx * 0.1}>
            <div
              className={`glass-card p-8 h-full flex flex-col relative border-2 ${
                plan.highlight ? 'border-brand-electric shadow-[0_0_30px_rgba(0,229,255,0.15)]' : 'border-brand-border/60'
              }`}
            >
              {plan.highlight && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-brand-electric text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                  Most Popular
                </div>
              )}
              <div className="text-sm font-bold text-white/60 uppercase tracking-wider mb-2">{plan.label}</div>
              <div className="text-2xl font-heading font-bold text-white mb-4">{plan.name}</div>
              <div className="flex items-baseline gap-1 mb-8">
                <span className="text-4xl font-black text-white">${isAnnual ? plan.priceYr : plan.priceMo}</span>
                <span className="text-white/60">/mo</span>
              </div>
              <button
                className={`w-full py-3 rounded-full font-bold mb-8 transition-all ${
                  plan.highlight
                    ? 'btn-cta'
                    : plan.name === 'Business'
                    ? 'border-2 border-brand-electric/60 hover:bg-brand-electric/10 text-brand-electric'
                    : 'bg-brand-card border-2 border-brand-border hover:bg-white/5 text-white'
                }`}
              >
                {plan.name === 'Business' ? 'Contact Sales' : 'Start Free Trial'}
              </button>
              <ul className="space-y-4 flex-grow">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-white/70">
                    <SafeIcon icon={FiCheck} className="text-brand-electric shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </FadeIn>
        ))}
      </div>

      <FadeIn className="max-w-3xl mx-auto">
        <h3 className="text-2xl font-bold text-center mb-8 text-white">Frequently Asked Questions</h3>
        <div className="space-y-4">
          {faqs.map((faq, idx) => (
            <div key={idx} className="glass-card p-6 border-2 border-brand-border/60">
              <h4 className="font-bold text-white mb-2">{faq.q}</h4>
              <p className="text-white/70 text-sm leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </FadeIn>
    </div>
  );
};

export default Pricing;